import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../components/ui/LegalPage';
import COMPANY, { ROUTES } from '../config/company';
import { SMS_CONSENT_TEXT } from '../components/ui/SmsConsentCheckbox';
import { SAMPLE_MESSAGES, MARKETING_SAMPLES, OPT_IN_CONFIRMATION, HELP_REPLY, STOP_REPLY } from './SmsOptIn';

const B = COMPANY.brand;
const LEGAL = COMPANY.legalName;

/*
 * Privacy Policy. The "SMS communications and consent" section carries every
 * disclosure 10DLC vetting looks for: message types, frequency, rates,
 * opt-in methods, opt-out (STOP), help (HELP), sample messages, the
 * no-third-party-sharing statement and a clear data-use / data-sharing /
 * contact explanation. Keep it in sync with the Telnyx campaign.
 */
const SECTIONS = [
  ['Introduction', [
    `This Privacy Policy explains how ${LEGAL} ("${B}", "we", "us", "our") collects, uses, stores and discloses personal information when you visit ${COMPANY.domainDisplay}, rent a trailer from us, contact us, or opt in to our SMS text-message program. By using our website or services you agree to the practices described here.`,
    `We are a trailer-rental company serving motor carriers and owner-operators. ${LEGAL} is located at ${COMPANY.hq.line1}, ${COMPANY.hq.line2}.`,
  ]],
  ['Information we collect', [
    'We collect only what we need to rent you a trailer and support you afterwards:',
    { list: [
      'Contact details: your name, company name, email address, mailing address and phone number(s).',
      'Carrier credentials: MC number, USDOT number, certificate of insurance, driver’s CDL and the signed rental agreement.',
      'Rental and billing records: reservations, pickup and return dates, invoices and payment status. We do not store full card numbers on our website.',
      'SMS consent submissions: your mobile number, date and time, form page, choices for customer-care and promotional messages, and the disclosure wording shown at submission.',
      'Usage data collected automatically when you use the website: IP address, browser and device type, pages viewed and referring page.',
    ] },
  ]],
  ['How we use your information', [
    { list: [
      'To process reservations, verify carrier authority and insurance, and prepare rental agreements.',
      'To communicate with you about your rental by phone, email and, if you have opted in, SMS.',
      'To invoice you and collect payment.',
      'To respond to your questions and resolve problems.',
      'To maintain records we are required to keep, prevent fraud and enforce our agreements.',
      'To improve our website and services.',
    ] },
  ]],
  ['SMS communications and consent', [
    <p key="intro">
      <strong>Your consent.</strong> {LEGAL} sends SMS text messages only to people who have opted in. Opting in is
      always optional and is never a condition of renting a trailer or purchasing any service. Phone numbers and
      consent collected for SMS are used only to send you the messages described below.
    </p>,
    <p key="optin"><strong>How to opt in (web form only).</strong> Enter your mobile number on our{' '}
      <Link to={ROUTES.smsOptIn}>SMS opt-in page</Link> ({COMPANY.domainDisplay}/sms-opt-in) or{' '}
      <Link to={ROUTES.contact}>Contact form</Link> ({COMPANY.domainDisplay}/contact) and check one or both of the
      separate consent boxes. Both boxes are unchecked by default and neither is required to submit the form.
      Our website server securely records the submission with a server-side UTC timestamp, form URL, separate consent choices and the exact disclosure wording applicable to each selected category.</p>,
    <p key="types"><strong>Types of messages and the consent wording for each.</strong></p>,
    { list: [
      <span key="c"><strong>Customer care messages</strong> (customer care replies, reservation confirmations, pickup and return reminders, account and billing notifications). Checkbox wording: &ldquo;{SMS_CONSENT_TEXT.customerCare}&rdquo;</span>,
      <span key="m"><strong>Marketing messages</strong> (trailer availability, rental specials and promotional offers). By checking this box you are opting in to marketing text messages from {LEGAL}. Checkbox wording: &ldquo;{SMS_CONSENT_TEXT.marketing}&rdquo;</span>,
    ] },
    <p key="confirm"><strong>Confirmation message.</strong> SMS program confirmation wording:</p>,
    { quote: OPT_IN_CONFIRMATION },
    <p key="samples"><strong>Example messages.</strong></p>,
    ...SAMPLE_MESSAGES.map((m) => ({ quote: m })),
    ...MARKETING_SAMPLES.map((m) => ({ quote: m })),
    <p key="freq">
      <strong>Message frequency.</strong> Message frequency may vary based on your rental activity. Marketing messages are limited to {COMPANY.sms.marketingFrequency}.
    </p>,
    <p key="fees">
      <strong>Fees.</strong> Message and data rates may apply according to your mobile carrier&rsquo;s pricing plan.{' '}
      {B} does not charge you for text messages.
    </p>,
    <p key="optout">
      <strong>How to opt out.</strong> Reply <strong>STOP</strong> to any SMS message at any time. You will receive one final
      confirmation and no further messages will be sent unless you opt in again:
    </p>,
    { quote: STOP_REPLY },
    <p key="help">
      <strong>Help.</strong> Reply <strong>HELP</strong> to any message, email{' '}
      {COMPANY.emailDisplay}:
    </p>,
    { quote: HELP_REPLY },
    <p key="share">
      <strong>No third-party sharing.</strong> Your mobile phone number and SMS opt-in consent will not be sold or
      shared with third parties or affiliates for marketing or promotional purposes. Messaging service providers may process your number and consent status only as necessary to deliver and manage our text-message program.
      These providers are not permitted to use that information for their own marketing.
    </p>,
    <p key="carrier">Carriers are not liable for delayed or undelivered messages.</p>,
  ], 'sms-communications'],
  ['Who we share information with', [
    'We do not sell personal information. We share it only with:',
    { list: [
      'Service providers that host our website, deliver form notification emails, process payments, or deliver SMS on our behalf strictly to provide those services, not for their own marketing.',
      'Carrier-verification services used to confirm MC/USDOT authority and insurance.',
      'Our insurers, auditors and professional advisers where needed.',
      'Law enforcement, regulators or courts when we are legally required to, or to protect our rights, property or safety.',
    ] },
    'Mobile information (phone numbers and SMS consent) is never shared with third parties or affiliates for their own marketing or promotional purposes.',
  ]],
  ['Cookies and analytics', [
    'Website hosting and site features may use essential cookies and technical logs. Where analytics tools are used, we use them to understand website performance. You can control cookies in your browser settings.',
  ]],
  ['How long we keep information', [
    'We keep rental agreements, carrier documents and billing records for as long as you have an account with us and afterwards for the period required by tax, insurance and transportation record-keeping rules. We retain opt-in evidence as needed to document your consent and comply with applicable messaging rules. Our website stores timestamped submissions and consent evidence in access-restricted server-side records outside the public web directory.',
  ]],
  ['How we protect information', [
    'We use encrypted connections (HTTPS), access controls that limit data to staff who need it, and reputable hosting and messaging providers. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.',
  ]],
  ['Your choices and rights', [
    { list: [
      'You may request a copy of the personal information we hold about you, ask us to correct it, or ask us to delete it where we are not required to keep it.',
      'You may opt out of SMS at any time by replying STOP, and out of marketing email by using the unsubscribe link or contacting us.',
      'Residents of states with consumer privacy laws may have additional rights; contact us to exercise them and we will respond within the time the law requires.',
    ] },
  ]],
  ["Children's privacy", [
    'Our services are for businesses and adults aged 18 and over. We do not knowingly collect personal information from anyone under 18.',
  ]],
  ['Changes to this policy', [
    'We may update this policy from time to time. The "last updated" date at the top of the page shows when it last changed. Material changes will be posted on this page.',
  ]],
  ['Contact us', [
    'Questions or requests about this Privacy Policy:',
    { list: [
      
      `Phone: ${COMPANY.phoneDisplay}`,
      `Email: ${COMPANY.emailDisplay}`,
      `${COMPANY.hq.label}: ${LEGAL}, ${COMPANY.hq.line1}, ${COMPANY.hq.line2}`,
    ] },
  ]],
];

export default function PrivacyPolicy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      lede="What we collect, why we need it, who we share it with, how SMS consent works, and what you can ask us to do with your information."
      sections={SECTIONS}
    />
  );
}
